import React, { useEffect, useState } from "react";
import { api, downloadPdf as downloadPdfFile } from "@/lib/api";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from "recharts";

const DAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export default function Reports() {
  const [kind, setKind] = useState("student");
  const [week, setWeek] = useState(null);
  const [busy, setBusy] = useState(null); // "weekly" | "monthly" | "biweekly" | null
  const [half, setHalf] = useState(new Date().getDate() <= 15 ? 1 : 2);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/attendance/week", { params: { kind } });
        if (!cancelled) setWeek(data);
      } catch (e) {
        if (!cancelled) console.error("week fetch failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const downloadPdf = async (which) => {
    if (busy) return;
    setBusy(which);
    const endpoints = { weekly: "/reports/weekly-pdf", monthly: "/reports/monthly-pdf", biweekly: "/reports/biweekly-pdf" };
    const endpoint = endpoints[which];
    const label = kind === "teacher" ? "docentes" : "alumnos";
    try {
      const params = { kind, tz };
      if (which === "biweekly") params.half = half;
      const ym = new Date().toISOString().slice(0, 7);
      let name;
      if (which === "monthly") name = `reporte_mensual_${label}_${ym}`;
      else if (which === "biweekly") name = `reporte_quincenal_${label}_${ym}_q${half}`;
      else name = `reporte_semanal_${label}_${(week?.week_start || "").slice(0, 10)}`;
      await downloadPdfFile(endpoint, { params, filename: `${name}.pdf` });
      toast.success("PDF generado");
    } catch (e) {
      toast.error("No se pudo generar el PDF. Intenta de nuevo en unos segundos.");
      console.error("pdf download failed", e);
    } finally {
      setBusy(null);
    }
  };

  const chartData = (week?.by_day || []).map(d => ({
    day: DAYS_ES[new Date(d.date + "T00:00:00Z").getUTCDay()],
    entradas: d.ins,
    salidas: d.outs,
    total: d.ins + d.outs,
  }));

  const personLabel = kind === "teacher" ? "docentes" : "alumnos";

  return (
    <div className="max-w-7xl mx-auto" data-testid="reports-page">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2">
            <span className="w-8 h-px bg-navy" /> Análisis · {personLabel}
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy">Reportes</h1>
          <p className="text-sm text-nardo-500 mt-2">Resumen semanal de asistencia y exportación en PDF, independiente para alumnos y docentes.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="export-pdf-button"
            onClick={() => downloadPdf("weekly")}
            disabled={!!busy}
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy === "weekly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {busy === "weekly" ? "Generando…" : "Descargar asistencia semanal"}
          </button>
          <div className="inline-flex items-stretch rounded-md overflow-hidden border border-navy">
            <select
              data-testid="biweekly-half-select"
              value={half}
              onChange={(e) => setHalf(Number(e.target.value))}
              disabled={!!busy}
              className="bg-white text-navy text-xs px-2 outline-none border-r border-navy/30 disabled:opacity-60"
            >
              <option value={1}>1ª quincena</option>
              <option value={2}>2ª quincena</option>
            </select>
            <button
              data-testid="export-biweekly-pdf-button"
              onClick={() => downloadPdf("biweekly")}
              disabled={!!busy}
              className="inline-flex items-center gap-2 text-navy hover:bg-navy hover:text-white px-4 py-2.5 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy === "biweekly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {busy === "biweekly" ? "Generando…" : "Quincenal"}
            </button>
          </div>
          <button
            data-testid="export-monthly-pdf-button"
            onClick={() => downloadPdf("monthly")}
            disabled={!!busy}
            className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-5 py-2.5 rounded-md transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy === "monthly" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {busy === "monthly" ? "Generando…" : "Descargar asistencia mensual"}
          </button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="inline-flex border border-nardo-200 rounded-md overflow-hidden bg-white mb-6">
        <button
          data-testid="tab-students"
          onClick={() => setKind("student")}
          className={`px-5 py-2 text-xs uppercase tracking-[0.25em] transition-colors ${kind === "student" ? "bg-navy text-white" : "text-nardo-600 hover:text-navy"}`}
        >Alumnos</button>
        <button
          data-testid="tab-teachers"
          onClick={() => setKind("teacher")}
          className={`px-5 py-2 text-xs uppercase tracking-[0.25em] transition-colors ${kind === "teacher" ? "bg-navy text-white" : "text-nardo-600 hover:text-navy"}`}
        >Docentes y Personal</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">Total registros</div>
          <div className="font-display font-extrabold text-4xl text-navy mt-2">{week?.total_records || 0}</div>
        </div>
        <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy-light" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">{kind === "teacher" ? "Docentes activos" : "Alumnos activos"}</div>
          <div className="font-display font-extrabold text-4xl text-navy mt-2">{week?.per_student?.length || 0}</div>
        </div>
        <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-nardo-400" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">Semana inicia</div>
          <div className="font-mono text-lg text-navy mt-2">{week?.week_start?.slice(0,10) || "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
          <h3 className="font-display font-semibold text-lg text-navy mb-6">Entradas vs salidas</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ left: -20 }}>
                <CartesianGrid stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="day" stroke="#7A7D80" fontSize={11} />
                <YAxis stroke="#7A7D80" fontSize={11} />
                <Tooltip contentStyle={{ border: "1px solid #C9CBCD", borderRadius: 6 }} />
                <Bar dataKey="entradas" fill="#112240" radius={[2,2,0,0]} />
                <Bar dataKey="salidas" fill="#1E3A8A" radius={[2,2,0,0]} fillOpacity={0.6} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-nardo-200 rounded-md p-6 relative">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy-light" />
          <h3 className="font-display font-semibold text-lg text-navy mb-6">Tendencia diaria</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: -20 }}>
                <CartesianGrid stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="day" stroke="#7A7D80" fontSize={11} />
                <YAxis stroke="#7A7D80" fontSize={11} />
                <Tooltip contentStyle={{ border: "1px solid #C9CBCD", borderRadius: 6 }} />
                <Line type="monotone" dataKey="total" stroke="#112240" strokeWidth={2} dot={{ r: 4, fill: "#1E3A8A" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-nardo-200 rounded-md mt-4">
        <div className="px-6 py-4 border-b border-nardo-200">
          <h3 className="font-display font-semibold text-lg text-navy">Por {kind === "teacher" ? "docente" : "alumno"}</h3>
        </div>
        <div className="divide-y divide-nardo-100">
          {(week?.per_student || []).length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-nardo-500">Sin registros esta semana</div>
          )}
          {(week?.per_student || []).map((s) => (
            <div key={s.student_id} className="px-6 py-3 grid grid-cols-3 gap-4 text-sm">
              <div className="font-medium text-navy">{s.name}</div>
              <div className="text-nardo-600">Entradas: <span className="font-mono text-navy">{s.ins}</span></div>
              <div className="text-nardo-600">Salidas: <span className="font-mono text-navy">{s.outs}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
